import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, styled, CircularProgress } from '@mui/material'
import { Close } from '@mui/icons-material'
import { DialogContent, IconButton, Typography } from '@carto/meridian-ds/components'
import { theme } from '@carto/meridian-ds/theme'

import { useIntlMessages } from 'app/localization/useIntlMessages'

import { CommentResponse, CommentWithResponses, Comment as CommentType, CommentAction, CommentUser } from '../../types'
import CommentActionsMenu from './CommentActionsMenu'
import CommentTextField, { CommentTextFieldValue } from './CommentTextField'
import Comment from './Comment'
import ResolveButton from './ResolveButton'

const HeaderBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: theme.spacing(1, 1, 1, 2),
  gap: theme.spacing(0.5)
}))

const CommentsList = styled(DialogContent)(() => ({
  overflowX: 'clip',
  gap: 0
}))

const CommentBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive'
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  backgroundColor: isActive ? theme.palette.primary.background : undefined,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1)
}))

const messageIds = {
  close: 'common.button.close',
  commentNo: 'collaboration.comments.popover.commentNo',
  addComment: 'collaboration.comments.popover.addComment',
  reply: 'collaboration.comments.popover.reply',
  replyWillReopen: 'collaboration.comments.popover.replyWillReopen'
}

export type CommentPopoverContentProps = {
  activeComment?: CommentWithResponses
  onCommentAction: (action: CommentAction) => void
  onResponseAction: (response: CommentResponse, action: CommentAction) => void
  onSubmit: (newComment: CommentTextFieldValue, editedElement?: CommentResponse | CommentType) => void
  onClose: () => void
  currentUserId: string
  isOwner?: boolean
  users: CommentUser[]
  scrollToLastResponse?: boolean
}

const emptyCommentTextFieldValue: CommentTextFieldValue = {
  content: '',
  mentions: []
}

function CommentPopoverContent({
  activeComment,
  currentUserId,
  isOwner,
  users,
  onCommentAction,
  onResponseAction,
  onSubmit,
  onClose,
  scrollToLastResponse
}: CommentPopoverContentProps) {
  const messages = useIntlMessages(messageIds)
  const textInputRef = useRef<HTMLInputElement | null>(null)
  const [newCommentDraft, setNewCommentDraft] = useState<CommentTextFieldValue>(emptyCommentTextFieldValue)

  const [editedElement, setEditedElement] = useState<CommentResponse | CommentType | undefined>()
  const [lastResponseRef, setLastResponseRef] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    setNewCommentDraft(emptyCommentTextFieldValue)
  }, [activeComment])

  useEffect(() => {
    if (scrollToLastResponse && lastResponseRef) {
      lastResponseRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [scrollToLastResponse, lastResponseRef])

  const usersAllowedForMentions = useMemo(
    () => users.filter((user) => user && !user.noAccess).map((user) => user),
    [users]
  )

  const handleCommentChange = (newComment) => {
    setNewCommentDraft(newComment)
  }

  const handleSubmitComment = () => {
    if (!newCommentDraft.content) return

    const toSubmit = newCommentDraft
    setEditedElement(undefined)
    setNewCommentDraft(emptyCommentTextFieldValue)
    onSubmit(toSubmit, editedElement)
  }

  const handleCommentAction = async (action: CommentAction) => {
    if (!activeComment?.id) return

    switch (action) {
      case 'edit':
        setEditedElement(activeComment)
        setNewCommentDraft(activeComment)
        textInputRef?.current?.focus()
        break
      default:
        onCommentAction(action)
        break
    }
  }

  const handleResponseAction = async (response: CommentResponse, action: CommentAction) => {
    if (!activeComment?.id || !response.id) return

    switch (action) {
      case 'edit':
        setEditedElement(response)
        setNewCommentDraft(response)
        textInputRef?.current?.focus()
        break
      default:
        onResponseAction(response, action)
        break
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      event.stopPropagation()
      if (editedElement) {
        setEditedElement(undefined)
        setNewCommentDraft(emptyCommentTextFieldValue)
      } else {
        onClose()
      }
    }
  }

  return (
    <>
      {activeComment?.id && (
        <>
          <HeaderBox>
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="subtitle2" color={activeComment.resolved ? 'text.secondary' : undefined}>
                {`${messages.commentNo}${activeComment.sequenceNumber + 1}`}
              </Typography>
            </Box>

            <Box flexGrow={1} />
            <CommentActionsMenu
              isThreadRoot
              onClick={handleCommentAction}
              canDelete={isOwner || activeComment.author.id === currentUserId}
              canEdit={false}
              resolved={activeComment.resolved}
            />
            <ResolveButton resolved={activeComment.resolved} onClick={() => handleCommentAction('toggleResolved')} />
            <IconButton onClick={onClose} icon={<Close />} tooltip={messages.close} aria-label={messages.close} />
          </HeaderBox>
          <CommentsList withGutter={false}>
            <CommentBox
              isActive={activeComment?.id === editedElement?.id}
              ref={!activeComment.responses?.length ? setLastResponseRef : null}
            >
              <Comment
                comment={{
                  ...activeComment,
                  responsesCount: 0,
                  resolved: false
                }}
                actions={
                  activeComment?.author.id === currentUserId && (
                    <CommentActionsMenu onClick={(action) => handleCommentAction(action)} canEdit />
                  )
                }
                users={users}
              />
            </CommentBox>
            {activeComment.responses || activeComment.responsesCount === 0 ? (
              activeComment.responses?.map((response, i) => (
                <CommentBox
                  key={response.id}
                  isActive={response.id === editedElement?.id}
                  ref={i === activeComment.responses?.length - 1 ? setLastResponseRef : null}
                >
                  <Comment
                    comment={response}
                    actions={
                      response.author.id === currentUserId && (
                        <CommentActionsMenu
                          onClick={(action) => handleResponseAction(response, action)}
                          canEdit
                          canDelete
                        />
                      )
                    }
                    users={users}
                  />
                </CommentBox>
              ))
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress color="inherit" />
              </Box>
            )}
          </CommentsList>
        </>
      )}
      <Box sx={{ p: 1 }}>
        <CommentTextField
          value={newCommentDraft}
          onChange={handleCommentChange}
          users={usersAllowedForMentions}
          onSubmit={handleSubmitComment}
          textInputRef={textInputRef}
          onKeyDown={handleKeyDown}
          isEditing={!!editedElement}
          autoFocus
          placeholder={
            activeComment?.id
              ? activeComment.resolved
                ? messages.replyWillReopen
                : messages.reply
              : messages.addComment
          }
        />
      </Box>
    </>
  )
}

export default CommentPopoverContent
